/*
 * Purpose: Onboarding banner — nudge users when first-run onboarding is incomplete
 * Last modified: 2026-06-26 | Agent
 * Completeness: 90
 */
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Wand2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { PRODUCT_TERMS } from '@/lib/productTerms';
import {
  readSetupSnapshot,
  setupProgressPercent,
  shouldShowSetupBanner,
} from '@/services/setup/setupStatus';

const DISMISS_KEY = 'autoblocks.setup.bannerDismissed';

export function SetupBanner() {
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const update = () => {
      const snap = readSetupSnapshot();
      const dismissed = sessionStorage.getItem(DISMISS_KEY) === '1';
      setVisible(shouldShowSetupBanner(snap) && !dismissed);
      setProgress(setupProgressPercent(snap));
    };
    update();
    const id = window.setInterval(update, 2000);
    return () => window.clearInterval(id);
  }, []);

  if (!visible) return null;

  return (
    <div className="shrink-0 border-b border-border bg-muted/30 px-4 py-3">
      <div className="max-w-4xl mx-auto flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Wand2 className="h-4 w-4 ui-icon shrink-0" />
            <span>
              Finish onboarding (vault + Gmail) to run {PRODUCT_TERMS.autoBlockFlow}s
            </span>
          </div>
          <Progress value={progress} className="h-1.5 max-w-md" />
        </div>
        <div className="flex gap-2 shrink-0">
          <Button size="sm" asChild>
            <Link to="/setup">Open {PRODUCT_TERMS.onboardingWizard}</Link>
          </Button>
          <Button
            size="sm"
            variant="ghost"
            aria-label="Dismiss for this session"
            onClick={() => {
              sessionStorage.setItem(DISMISS_KEY, '1');
              setVisible(false);
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
